import {Component, HostListener} from '@angular/core';
import {ProductService} from "../../../service/product.service";
import {SnackBarUtil} from "../../../qr-copy-message/snack-bar-util.service";
import {MockupDataModel} from "../../../qr-copy-message/mockup-data.model";
import {ProductResponse} from "../../../models/productResponse";
import {Page} from "../../../interface/hashtag";
import {MatDialog} from "@angular/material/dialog";
import {DeleteProfileCardComponent} from "../../../profile-card/delete-profile-card/delete-profile-card.component";
import {Router} from "@angular/router";
import {ProductAvailability} from "./product-availability.enum";
import {GetPageUtil} from "../../get-page-util";

@Component({
  selector: 'app-admin-products',
  templateUrl: './admin-products.component.html',
  styleUrl: './admin-products-common.component.scss'
})
export class AdminProductsComponent {

  private readonly PRODUCT_MANAGE_URL = 'admin/page/shop/products/manage';
  protected readonly AUTO_SUBSCRIPTION_PRODUCT_ID = 9;

  protected products: Array<ProductResponse> = [];
  protected productsCount: number = 0;
  protected currentPage: number = 0;
  protected productSize: number = 10;
  protected totalPages: number = 0;
  protected isLoading: boolean = false;
  private getPageUtil = new GetPageUtil();

  protected selectedProductId: number = 0;

  constructor(
    private productService: ProductService,
    private snackBarUtil: SnackBarUtil,
    private dialog: MatDialog,
    private router: Router
  ) {
  }

  ngOnInit() {
    this.initAllProducts(true);
  }

  get isPaginationEnabled(): boolean {
    if(!this.totalPages) return false;
    return this.currentPage < this.totalPages;
  }

  protected deleteProduct() {
    if(!this.selectedProductId) {
      this.showSnackbar(MockupDataModel.error("Something went wrong."), SnackBarUtil.CLASSES.error);
      return;
    }

    const dialogRef = this.dialog.open(DeleteProfileCardComponent, {
      data: {
        deleteTitle: "Are you sure you want to delete this product?"
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.productService.delete(this.selectedProductId).subscribe({
            next: () => {
              this.products = this.products.filter(product => product.id !== this.selectedProductId);
              this.productsCount--;
              this.selectedProductId = 0;
              this.showSnackbar(MockupDataModel.success("Product successfully deleted."), SnackBarUtil.CLASSES.success);
            },
            error: (error) => {
              const errorMessage = error?.error?.message || "Something went wrong.";
              this.showSnackbar(MockupDataModel.error(errorMessage), SnackBarUtil.CLASSES.error);
            }
          });
        }
      }
    })
  }

  protected setSelectedProductId(id: number) {
    this.selectedProductId = id;
  }

  protected onClickAddProduct() {
    this.router.navigateByUrl(this.PRODUCT_MANAGE_URL).catch();
  }

  protected redirectToEditPage() {
    if(this.selectedProductId) {
      const url = `${this.PRODUCT_MANAGE_URL}?id=${this.selectedProductId}`;
      this.router.navigateByUrl(url).catch();
    }
  }

  private initAllProducts(isFirstTime: boolean) {
    this.isLoading = true;
    const page = isFirstTime ? 0 : this.currentPage;
    this.productService.getAll(page).subscribe({
      next: (products: Page<ProductResponse>) => {
        this.loadProductsMainPictures(products);
        this.products = products.content;
        this.isLoading = false;
        if(isFirstTime) {
          this.productsCount = products.totalElements;
          this.totalPages = products.totalPages;
        }
      },
      error: () => {
        this.isLoading = false;
      }
    })
  }

  private loadProductImage(product: ProductResponse): void {
    product.isMainImageLoaded = false;
    const imageUrl = product.imageUrls?.at(0);
    if (imageUrl) {
      this.productService.getPictureByName(imageUrl).subscribe({
        next: (fileResponse: Blob) => {
          product.mainImageSafeUrl = URL.createObjectURL(fileResponse);
          product.isMainImageLoaded = true;
        },
        error: () => {
          product.isMainImageLoaded = false;
        }
      });
    }
  }

  private loadProductsMainPictures(products: Page<ProductResponse>) {
    products.content.forEach(product => {
      product.isMainImageLoaded = false;
      const imageUrl = product.imageUrls.at(0);
      if (imageUrl) {
        this.productService.getPictureByName(imageUrl).subscribe({
          next: (fileResponse: Blob) => {
            product.mainImageSafeUrl = URL.createObjectURL(fileResponse);
            product.isMainImageLoaded = true;
          }
        })
      }
    });
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.initAllProducts(false);
    }
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.currentPage, this.productsCount, this.productSize);
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  protected readonly ProductAvailability = ProductAvailability;
}
